import {Component, ElementRef, HostListener, Injector, OnDestroy, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {fadeAnimation} from 'animations';

import {Offer, ResponsePrecontractInfo, StepData, ValidationError} from 'models';
import {of, Subscription} from 'rxjs';
import {catchError, debounceTime, first} from 'rxjs/operators';
import {ModalType} from '../../../../../../app-shared/models/modal.interface';
import {SimTypeEnum} from '../../../../../../app-shared/models/steps/step-sim.interface';
import {ProcessStepBaseComponent} from '../../_core/process-step-base.component';


@Component({
  selector: 'sb-step-customer',
  templateUrl: './customer.component.html',
  styleUrls: [ './customer.component.scss' ],
  animations: [ fadeAnimation ]
})
export class CustomerComponent extends ProcessStepBaseComponent implements OnInit, OnDestroy {
  public simTypes: SimTypeEnum[];
  public SIM_TYPES = SimTypeEnum;
  public simType: SimTypeEnum;
  public showTooltip = false;
  public showTaxCodeError = false;
  private previousStepIdSubscription: Subscription;
  public precontractUrl = '';
  public showFirstNameError = false;
  public showLastNameError = false;

  constructor(injector: Injector, private eRef: ElementRef, private router: Router) {
    super(injector);
  }

  private manageSimTypes(): void {
    /*this.subscriptions.add(
      this.appStoreFacadeService.entity.availableSimTypes$
        .pipe(first(Boolean))
        .subscribe(({ types }) => {
          this.simTypes = types;
        })
    );*/
    this.subscriptions.add(
      this.appStoreFacadeService.entity.simType$
        .pipe(first(Boolean))
        .subscribe(({ value }) => {
          const simType = value && value.type;
          this.simType = simType;
          if (this.simType === SimTypeEnum.IdleSim) {
              this.formGroup.get('generalConsents').disable();
          }
        })
    );
  }

  private managePreContract() {
    this.precontractUrl = this.environmentService.getPrecontractDefaultUrl();

    this.subscriptions.add(
      this.appStoreFacadeService.entity.offer$
        .pipe(first(Boolean))
        .subscribe((offer: Offer) => {
          this.execService
            .getPrecontractInfo(offer.productId || offer.ropz)
            .pipe(catchError(err => {
              return of(null);
          })).subscribe((resp: ResponsePrecontractInfo) => {
            this.precontractUrl = (resp && (resp.filenameLink || resp.link)) ||
              this.environmentService.getPrecontractDefaultUrl();
          });
        })
    );
  }

  ngOnInit() {
    super.setFormGroup(this.formsService.generateCustomerForm());
    super.setFormStoreDataFunction(this.appStoreFacadeService.step.storeStepCustomerValue);
    super.isStickyNavigation();
    this.getFormDataAndValidation();
    this.manageSimTypes();
    this.managePreContract();
    this.handleUniqueConsents();
    this.trackTaxCodeChanges();
    this.validateFirstnameLastnameTaxcode();
  }

  /* public goToPreviousStep() {
    if (!this.simType || this.simType !== this.SIM_TYPES.IdleSim) {
      // Overrides because it should redirect to another place, instead of the previous step.
      this.appStoreFacadeService.navigation.goPrevious();
    } else {
      return super.goToPreviousStep();
    }
  } */
    public goToPreviousStep() {
      if (this.simType === this.SIM_TYPES.IdleSim) {
        return super.goToPreviousStep();
      }
      this.previousStepIdSubscription = this.appStoreFacadeService.navigation.previousStepId$
        .pipe(first())
        .subscribe((previousStep) => {
          if (previousStep === 'sim-type') {
            super.goToPreviousStep();
          } else {
            this.appStoreFacadeService.navigation.goPrevious();
          }
        });
    }

  private getFormDataAndValidation() {
    this.subscriptions.add(
      this.appStoreFacadeService.step.customer$
        .subscribe((customerStepData: StepData) => {
          const result = super.setFormGroupStepData(customerStepData);
          console.warn(result);
          super.setFormGroupStepData(customerStepData);
        })
    );

    this.subscriptions.add(
      this.appStoreFacadeService.step.customerValue$
        .pipe( debounceTime(150), first(Boolean))
        .subscribe(() => {
          this.sendAnalyticsData();
        })
    );
  }

  private handleUniqueConsents() {
    const generalConsentsFormGroup = this.formGroup.get('generalConsents') as FormGroup;
    const generalServiceField = generalConsentsFormGroup.get('generalServiceConditionsAccepted');
    generalConsentsFormGroup.patchValue({
      precontractConditionsAccepted: generalServiceField.value
    });

    this.subscriptions.add(
      generalServiceField.valueChanges.subscribe((value) => {
        generalConsentsFormGroup.patchValue({
          precontractConditionsAccepted: value
        });
      })
    );
  }

  public get formGroupInvalid() {
    return this.formGroup.invalid ||
      (this.simType === SimTypeEnum.eSim && !this.formGroup.get('generalConsents.eSimConditionsAccepted').value) ||
      (this.simType !== SimTypeEnum.IdleSim && !this.formGroup.get('generalConsents.precontractConditionsAccepted').value);
  }

  private trackTaxCodeChanges(): void {
    const taxCodeControl = this.formGroup.get('customerIdentity.taxCode');
    this.showTaxCodeError = false;
    taxCodeControl.valueChanges.pipe(debounceTime(200)).subscribe(() => {
      if (taxCodeControl.value && taxCodeControl.value.length < 16) {
        this.showTaxCodeError = false;
      }
    });
    taxCodeControl.statusChanges.subscribe((status) => {
      if (taxCodeControl.value && taxCodeControl.value.length === 16) {
        if (taxCodeControl.invalid && taxCodeControl.touched) {
          this.showTaxCodeError = true;
        } else {
          this.showTaxCodeError = false;
        }
      }
    });
  }

  get shouldShowTaxCodeHelper() {
    // tslint:disable-next-line:max-line-length
    return true;
  }

  private validateFirstnameLastnameTaxcode() {
    this.subscriptions.add(
      this.appStoreFacadeService.errors.inputValidationErrors$
        .subscribe((inputValidationError: ValidationError) => {
          if (inputValidationError && typeof inputValidationError.path === 'string') {
            this.showFirstNameError = inputValidationError.path === 'customer-identity.firstName';
            this.showLastNameError = inputValidationError.path === 'customer-identity.lastName';
            this.showTaxCodeError = inputValidationError.path === 'customer-identity.taxCode';
          }
        })
    );
  }

  public clearError(field: string): void {
    setTimeout(() => {
      switch (field) {
        case 'firstName':
          this.showFirstNameError = false;
          this.formGroup.get('customerIdentity.firstName').setErrors(null);
          break;
        case 'lastName':
          this.showLastNameError = false;
          this.formGroup.get('customerIdentity.lastName').setErrors(null);
          break;
        case 'taxCode':
          this.showTaxCodeError = false;
          this.formGroup.get('customerIdentity.taxCode').setErrors(null);
          break;
      }

      if (!this.showFirstNameError && !this.showLastNameError && !this.showTaxCodeError) {
        const subtitleElement = document.querySelector('.sb-form-block__subtitle');
        if (subtitleElement) {
          subtitleElement.classList.remove('cf-error');
        }
      }
    }, 0);
  }

  calculateTaxCode() {
    this.appStoreFacadeService.modals
      .openModal(ModalType.TaxCodeCalculation, {
        customer: {
          firstName: this.formGroup.get('customerIdentity.firstName').value,
          lastName: this.formGroup.get('customerIdentity.lastName').value
        },
        onSuccess: ({ taxCode, lastName, firstName }) => {
          this.formGroup.get('customerIdentity.firstName').patchValue(firstName);
          this.formGroup.get('customerIdentity.lastName').patchValue(lastName);
          this.formGroup.get('customerIdentity.taxCode').patchValue(taxCode);
        }
      });
  }

  toggleTooltip(event: MouseEvent): void {
    event.stopPropagation();
    this.showTooltip = !this.showTooltip;
  }

  closeTooltip(): void {
    this.showTooltip = false;
  }

  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement: any): void {
    const clickedInside = this.eRef.nativeElement.contains(targetElement);
    if (clickedInside) {
      this.showTooltip = false;
    }
  }

  ngOnDestroy(): void {
    if (this.previousStepIdSubscription) {
      this.previousStepIdSubscription.unsubscribe();
    }
  }
  showCompatibleDevices() {
    this.appStoreFacadeService.modals.openEsimCompatibleDevicesModal();
  }
}
